import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingContainer from 'components/common/loadingContainer/LoadingContainer';
import { Languages } from 'Constants/Common';
import { useAppSelector } from 'state/hooks';
import {
    CustomOptions,
    FlowCallbackData,
    WPLibrary,
    ResultCallbackData
} from './types';
import { selectSiteCountry } from 'state/selectors/site';

export type WorldPayProps = {
    paymentUrl: string;
    onPageReady: () => void;
    onPaymentSuccess?: (orderKey: string) => void;
    onPaymentError?: (order: ResultCallbackData) => void;
};

const WorldPayForm = ({
    paymentUrl,
    onPageReady,
    onPaymentSuccess,
    onPaymentError
}: WorldPayProps): JSX.Element => {
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [scriptError, setScriptError] = useState(false);

    const { t, i18n } = useTranslation();

    const countryCode = useAppSelector(selectSiteCountry);
    const language = i18n.language;

    const wp = useRef<WPLibrary>();

    const loadScript = () => {
        const loadError = () => {
            setScriptError(true);
        };

        const onScriptLoaded = () => {
            onPageReady();
        };

        const externalScript = document.createElement('script');
        externalScript.id = 'worldPay';
        externalScript.onerror = loadError;
        externalScript.async = true;
        externalScript.type = 'text/javascript';
        externalScript.setAttribute('crossorigin', 'anonymous');
        externalScript.onload = onScriptLoaded;
        document.head.appendChild(externalScript);
        externalScript.src =
            'https://payments.worldpay.com/resources/hpp/integrations/embedded/js/hpp-embedded-integration-library.js';
    };

    const setupWp = () => {
        const flowCallback = (result: FlowCallbackData) => {
            console.log(`flowCallback: ${JSON.stringify(result)}`);
        };

        const resultCallback = (responseData: ResultCallbackData) => {
            const status = responseData.order?.status;

            if (status === 'success') {
                onPaymentSuccess?.(responseData.order?.orderKey);
            } else {
                onPaymentError?.(responseData);
            }
        };

        const customOptions: CustomOptions = {
            url: paymentUrl,
            type: 'iframe',
            target: 'wp-container',
            inject: 'immediate',
            accessibility: true,
            debug: false,
            language: language === Languages.At ? Languages.De : language,
            country: countryCode,
            flowCallback,
            resultCallback
        };

        //@ts-ignore
        wp.current = new WPCL.Library();
        wp.current?.setup(customOptions);
    };

    const cleanup = () => {
        try {
            const wpContainer = document.getElementById('wp-container');
            if (wpContainer) {
                wp?.current?.destroy?.();
            }
        } catch (e) {
            console.warn('WPCL destroy failed', e);
        }
    };

    useEffect(() => {
        loadScript();

        return cleanup;
    }, []);

    useEffect(() => {
        if (!paymentUrl || showPaymentForm) {
            return;
        }

        setupWp();

        setShowPaymentForm(true);
    }, [paymentUrl, showPaymentForm]);

    if (scriptError) {
        return <>ERROR</>;
    }

    return (
        <>
            {!showPaymentForm && (
                <LoadingContainer loading={true} message={t('loading')} />
            )}
            <div id='wp-container' data-testid='worldpay-iframe'></div>
        </>
    );
};

export default WorldPayForm;
